import { getSpeakerColor as getSpeakerColorFromDatoId } from '@src/services/SpeakerManagementService';
import { injectSpeakerLabel } from '@src/stores-v2/speakerCache';
import { useTranscription } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import {
  indexRange,
  isSpeakerReassigned,
  relabelRange,
  restoreRange,
  withTrailingMarks,
} from '@src/utility/speakerRelabel';
import { Uploader } from '@src/utility/uploader';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { SpeakerWithColor } from '@src/services/SpeakerManagementService';
import type { TranscriptElement } from '@src/types.ts/transcript';

const logger = LogManager.getLogger('hook:useSpeakerOverrides');

// ── Shared utilities ────────────────────────────────────────────────
// Which elements take part in speaker assignment, and how a range is
// relabelled / restored, is defined ONCE in `@src/utility/speakerRelabel.ts`
// (PLAT-11670). Words and labelled punctuation move together; whitespace and
// unlabelled marks are never touched.

/** Check whether a single transcript element (word or mark) has been section-reassigned. */
export function isElementReassigned(el: TranscriptElement): boolean {
  return isSpeakerReassigned(el);
}

/**
 * Restore original speaker labels on the given elements at the given indices.
 * Returns a new array (shallow copy) with the restored labels.
 *
 * Widened by the same trailing-mark rule as `applySpeakerOverride`, so an undo
 * covers exactly what the write covered. If the write reached the closing full
 * stop and the undo did not, the undo would leave a two-speaker row behind with
 * no way back (blind review of PLAT-11670, finding 6).
 */
export function restoreOriginalSpeakerLabels(
  elements: TranscriptElement[],
  indices: number[],
): TranscriptElement[] {
  return restoreRange(elements, withTrailingMarks(elements, indices));
}

/**
 * Apply a speaker override to elements at the given indices.
 * Returns a new array (shallow copy) with updated labels.
 *
 * The range comes from a browser text selection, which stops at the last
 * character of the last word — so the sentence's closing full stop sits one
 * index past it and would keep the previous speaker's name. The row would then
 * carry two names, which is exactly what makes the Translation Review check
 * refuse with "Fix one speaker at a time" (the bug PLAT-11670 was filed for).
 * `withTrailingMarks` widens the range to the marks that belong to the selected
 * words, and only to those.
 */
export function applySpeakerOverride(
  elements: TranscriptElement[],
  startIndex: number,
  endIndex: number,
  newLabel: string,
): TranscriptElement[] {
  return relabelRange(
    elements,
    withTrailingMarks(elements, indexRange(startIndex, endIndex)),
    newLabel,
  );
}

// ── Undo/Redo diff format ───────────────────────────────────────────

interface ElementDiff {
  index: number;
  oldLabel: string | undefined;
  oldOriginalLabel: string | undefined;
  newLabel: string | undefined;
  newOriginalLabel: string | undefined;
}

function computeDiff(
  before: TranscriptElement[],
  after: TranscriptElement[],
): ElementDiff[] {
  const diffs: ElementDiff[] = [];
  const len = Math.max(before.length, after.length);
  for (let i = 0; i < len; i++) {
    const b = before[i];
    const a = after[i];
    if (
      b?.speaker_label !== a?.speaker_label ||
      b?.original_speaker_label !== a?.original_speaker_label
    ) {
      diffs.push({
        index: i,
        oldLabel: b?.speaker_label,
        oldOriginalLabel: b?.original_speaker_label,
        newLabel: a?.speaker_label,
        newOriginalLabel: a?.original_speaker_label,
      });
    }
  }
  return diffs;
}

function applyDiff(
  elements: TranscriptElement[],
  diffs: ElementDiff[],
  direction: 'undo' | 'redo',
): TranscriptElement[] {
  const updated = [...elements];
  for (const d of diffs) {
    if (!updated[d.index]) continue;
    updated[d.index] = {
      ...updated[d.index],
      speaker_label: direction === 'undo' ? d.oldLabel : d.newLabel,
      original_speaker_label:
        direction === 'undo' ? d.oldOriginalLabel : d.newOriginalLabel,
    };
  }
  return updated;
}

// ── Hook ────────────────────────────────────────────────────────────

export function useSpeakerOverrides() {
  const videoCreator = useVideoCreatorStore();
  const transcriptionStore = useTranscription();

  // Stores diffs (not full arrays) for memory efficiency
  const undoStack = useRef<ElementDiff[][]>([]);
  const redoStack = useRef<ElementDiff[][]>([]);
  const [stackVersion, setStackVersion] = useState(0);

  const hasUndo = undoStack.current.length > 0;
  const hasRedo = redoStack.current.length > 0;

  // Debounced background save — clean up timer on unmount to prevent stale state access
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);
  const saveInBackground = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const storyId = videoCreator.story?.id;
      const transcription = transcriptionStore?.getOriginalTranscription();
      if (!storyId || !transcription) return;

      const jsonString = JSON.stringify(transcription, null, 2);
      const fileName = 'transcription.json';
      const fileKey = `sessions/${storyId}/videos/${fileName}`;
      const file = new File([jsonString], fileName, {
        type: 'application/json',
        lastModified: Date.now(),
      });

      const uploader = new Uploader({
        file,
        fileName,
        fileKey,
        baseURL: `${process.env.REACT_APP_API_URL}/api/upload`,
        chunkSize: 10,
        threadsQuantity: 1,
        useTransferAcceleration: true,
        onCompleteFn: () => logger.info('Transcript saved in background'),
        onError: (error: Error) =>
          logger.error('Background transcript save failed', error),
      });
      uploader.start();
    }, 500);
  }, [videoCreator.story?.id, transcriptionStore]);

  /** Commit current elements, apply a mutation, push diff to undo stack, save. */
  const commitOverride = useCallback(
    (mutate: (elements: TranscriptElement[]) => TranscriptElement[]) => {
      if (!transcriptionStore) return;
      const orig = transcriptionStore.getOriginalTranscription();
      if (!orig?.elements) return;

      const before = orig.elements;
      const after = mutate(before);
      const diff = computeDiff(before, after);
      if (diff.length === 0) return;

      undoStack.current.push(diff);
      redoStack.current = [];
      setStackVersion(v => v + 1);

      transcriptionStore.replaceTranscriptVersion({
        ...orig,
        elements: after,
      });
      saveInBackground();
    },
    [transcriptionStore, saveInBackground],
  );

  const undo = useCallback(() => {
    if (!transcriptionStore || undoStack.current.length === 0) return;
    const orig = transcriptionStore.getOriginalTranscription();
    if (!orig?.elements) return;

    const diff = undoStack.current.pop()!;
    redoStack.current.push(diff);
    setStackVersion(v => v + 1);

    transcriptionStore.replaceTranscriptVersion({
      ...orig,
      elements: applyDiff(orig.elements, diff, 'undo'),
    });
    saveInBackground();
  }, [transcriptionStore, saveInBackground]);

  const redo = useCallback(() => {
    if (!transcriptionStore || redoStack.current.length === 0) return;
    const orig = transcriptionStore.getOriginalTranscription();
    if (!orig?.elements) return;

    const diff = redoStack.current.pop()!;
    undoStack.current.push(diff);
    setStackVersion(v => v + 1);

    transcriptionStore.replaceTranscriptVersion({
      ...orig,
      elements: applyDiff(orig.elements, diff, 'redo'),
    });
    saveInBackground();
  }, [transcriptionStore, saveInBackground]);

  /** Reassign a range of elements to a new speaker. */
  const reassignSection = useCallback(
    (
      startIndex: number,
      endIndex: number,
      newLabel: string,
      datoSpeakerId: string,
      speakerLists: {
        storySpeakers: SpeakerWithColor[];
        orgSpeakers: SpeakerWithColor[];
      },
    ) => {
      const storyId = videoCreator.story?.id;

      commitOverride(elements =>
        applySpeakerOverride(elements, startIndex, endIndex, newLabel),
      );

      // Inject into speaker cache for immediate name resolution
      const selectedSpeaker = [
        ...speakerLists.storySpeakers,
        ...speakerLists.orgSpeakers,
      ].find(s => s.id === datoSpeakerId);
      if (selectedSpeaker && storyId) {
        injectSpeakerLabel(
          storyId,
          newLabel,
          selectedSpeaker.name || 'Unknown',
          getSpeakerColorFromDatoId(datoSpeakerId, 0),
        );
      }
    },
    [videoCreator.story?.id, commitOverride],
  );

  /** Undo a section override by restoring original labels. */
  const undoSectionReassign = useCallback(
    (indices: number[]) => {
      commitOverride(elements =>
        restoreOriginalSpeakerLabels(elements, indices),
      );
    },
    [commitOverride],
  );

  return {
    hasUndo,
    hasRedo,
    undo,
    redo,
    reassignSection,
    undoSectionReassign,
    commitOverride,
    /** Exposed for version-dependent re-renders (e.g., button state) */
    stackVersion,
  };
}
